import React, { useEffect, useState } from "react";
import {
  Grid,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Slide,
  Box,
} from "@mui/material";
import { Add, Close } from "@mui/icons-material";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import axios from "axios";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useNavigate } from "react-router-dom";
import { API_ENDPOINTS, getUploadUrl } from "config/api";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import safeToast from "utils/safeToast";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import confetti from "canvas-confetti";
import { decryptText } from "config/encryption";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const ChapterList = () => {
  const [chapters, setChapters] = useState([]);
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [studentList, setStudentList] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [subscribingId, setSubscribingId] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingSubscribeChapter, setPendingSubscribeChapter] = useState(null);
  const [unsubscribeDialogOpen, setUnsubscribeDialogOpen] = useState(false);
  const [pendingUnsubscribeChapter, setPendingUnsubscribeChapter] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileDetails, setProfileDetails] = useState(null);
  const [filter, setFilter] = useState("");

  const fetchChapters = async () => {
    try {
      const encrypted = localStorage.getItem("UserLogDetails");
      const decryptedUser = decryptText(encrypted);
      if (!decryptedUser) {
        safeToast.error("Session expired. Please login again.");
        navigate("/authentication/sign-in");
        return;
      }

      const { Token, StudentId } = JSON.parse(decryptedUser);
      const res = await axios.get(
        API_ENDPOINTS.PROFILE.FETCH_CHAPTERS(0, StudentId),
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        }
      );
      setChapters(res.data);
    } catch (err) {
      console.error("Error fetching chapters", err);
    }
  };

  const fetchChapterStudents = async (chapterId) => {
    setLoadingStudents(true);
    try {
      const encrypted = localStorage.getItem("UserLogDetails");
      const decryptedUser = decryptText(encrypted);
      const { Token } = JSON.parse(decryptedUser);
      const res = await axios.get(
        API_ENDPOINTS.PROFILE.FETCH_CHAPTER_MEMBERS(chapterId),
        { headers: { Authorization: `Bearer ${Token}` } }
      );
      setStudentList(res.data);
    } catch (err) {
      setStudentList([]);
    } finally {
      setLoadingStudents(false);
    }
  };

  // Use the 'sub' property from the API response to check subscription
  const isSubscribed = (chapter) => !!chapter.sub;

  const handleSubscribe = (chapter) => {
    if (isSubscribed(chapter)) {
      setPendingUnsubscribeChapter(chapter);
      setUnsubscribeDialogOpen(true);
    } else {
      setPendingSubscribeChapter(chapter);
      setConfirmDialogOpen(true);
    }
  };

  const confirmSubscribe = async () => {
    if (!pendingSubscribeChapter || isProcessing) return;
    setIsProcessing(true);
    const chapter = pendingSubscribeChapter;
    setSubscribingId(chapter.chapterId);
    setConfirmDialogOpen(false);
    try {
      const encrypted = localStorage.getItem("UserLogDetails");
      const decryptedUser = decryptText(encrypted);
      const { StudentId, Token } = JSON.parse(decryptedUser);
      const payload = {
        chapterMemberId: 0,
        chapterId: chapter.chapterId,
        studentId: StudentId,
        roleId: 4,
        isCoordinator: false,
        isActive: true,
        joinedDate: new Date().toISOString(),
      };
      const res = await axios.post(
        API_ENDPOINTS.PROFILE.MERGE_CHAPTER_MEMBER,
        payload,
        { headers: { Authorization: `Bearer ${Token}` } }
      );
      // Update the chapter's 'sub' property locally after successful subscription
      setChapters((prev) =>
        prev.map((c) =>
          c.chapterId === chapter.chapterId
            ? { ...c, sub: true, mc: c.mc ? c.mc + 1 : 1 }
            : c
        )
      );
      // If the dialog is open for this chapter, refresh the member list
      if (dialogOpen && selectedChapter && selectedChapter.chapterId === chapter.chapterId) {
        fetchChapterStudents(chapter.chapterId);
      }
      setSnackbar({ open: true, message: String(res.data?.message || "Subscribed to chapter!"), severity: 'success' });
      // Celebrate with confetti
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
      });
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to subscribe.", severity: 'error' });
    } finally {
      setSubscribingId(null);
      setPendingSubscribeChapter(null);
      setIsProcessing(false);
    }
  };

  const confirmUnsubscribe = async () => {
    if (!pendingUnsubscribeChapter || isProcessing) return;
    setIsProcessing(true);
    const chapter = pendingUnsubscribeChapter;
    setSubscribingId(chapter.chapterId);
    setUnsubscribeDialogOpen(false);
    try {
      const encrypted = localStorage.getItem("UserLogDetails");
      const decryptedUser = decryptText(encrypted);
      const { StudentId, Token } = JSON.parse(decryptedUser);
      const res = await axios.post(
        API_ENDPOINTS.PROFILE.DELETE_CHAPTER_MEMBER,
        {
          chapterId: chapter.chapterId,
          studentId: StudentId,
        },
        { headers: { Authorization: `Bearer ${Token}` } }
      );
      setChapters((prev) =>
        prev.map((c) =>
          c.chapterId === chapter.chapterId
            ? { ...c, sub: false, mc: c.mc > 0 ? c.mc - 1 : 0 }
            : c
        )
      );
      setSnackbar({ open: true, message: String(res.data?.message || "Unsubscribed from chapter!"), severity: 'error' });
      // If the dialog is open for this chapter, refresh the member list
      if (dialogOpen && selectedChapter && selectedChapter.chapterId === chapter.chapterId) {
        fetchChapterStudents(chapter.chapterId);
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to unsubscribe.", severity: 'error' });
    } finally {
      setSubscribingId(null);
      setPendingUnsubscribeChapter(null);
      setIsProcessing(false);
    }
  };

  const handleSnackbarClose = () => setSnackbar((prev) => ({ ...prev, open: false }));

  const fetchProfileDetails = async (studentId) => {
    setProfileLoading(true);
    setProfileDetails(null);
    try {
      const encrypted = localStorage.getItem("UserLogDetails");
      const decryptedUser = decryptText(encrypted);
      const { Token } = JSON.parse(decryptedUser);
      // Replace with your actual API endpoint for fetching profile details
      const res = await axios.get(
        API_ENDPOINTS.PROFILE.GET_STUDENT_PROFILE(studentId),
        { headers: { Authorization: `Bearer ${Token}` } }
      );
      setProfileDetails(res.data);
    } catch (err) {
      setProfileDetails(null);
    } finally {
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    fetchChapters();
  }, []);

  // Enhanced filter logic
  const filteredChapters = filter
    ? chapters.filter(chapter => {
      if (!chapter.chapterName) return false;
      const name = chapter.chapterName.toLowerCase();
      return filter
        .toLowerCase()
        .split(" ")
        .every(word => name.includes(word));
    })
    : chapters;

  const isFiltering = !!filter.trim();
  const marqueeChapters = isFiltering ? filteredChapters : [...filteredChapters, ...filteredChapters];

  const getHighlightedName = (name, filter) => {
    if (!filter) return name;
    const idx = name.toLowerCase().indexOf(filter.toLowerCase());
    if (idx === -1) return name;
    return (
      <>
        {name.substring(0, idx)}
        <span style={{
          background: '#ffe082',
          color: '#1976d2',
          borderRadius: 4,
          padding: '0 2px',
          fontWeight: 700
        }}>
          {name.substring(idx, idx + filter.length)}
        </span>
        {name.substring(idx + filter.length)}
      </>
    );
  };

  return (

    <MDBox>
      <MDTypography variant="h6" fontWeight={500} gutterBottom>
        Alumni Chapters
      </MDTypography>

      <MDBox mb={2} sx={{ maxWidth: 280 }}>
        <Autocomplete
          freeSolo
          options={chapters.map((c) => c.chapterName)}
          value={filter}
          size="small" // Make Autocomplete small
          onInputChange={(event, newInputValue) => setFilter(newInputValue || "")}
          onChange={(event, newValue) => setFilter(newValue || "")}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Filter by Chapter Name"
              variant="outlined"
              size="small" // Make TextField small
              fullWidth
              sx={{
                minHeight: 36,
                fontSize: 13,
                "& .MuiInputBase-root": { minHeight: 36, fontSize: 13 },
                "& .MuiInputLabel-root": { fontSize: 13 },
              }}
            />
          )}
        />
      </MDBox>

      <Box sx={{
        width: '100%',
        overflowX: 'hidden',
        position: 'relative',
        pb: 2
      }}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            animation: !isFiltering ? 'scroll-horizontal 20s linear infinite' : 'none',
            '@keyframes scroll-horizontal': {
              '0%': { transform: 'translateX(0)' },
              '100%': { transform: 'translateX(-100%)' },
            },
          }}
        >
          {marqueeChapters.map((chapter) => (
            <Box key={chapter.chapterId} sx={{ minWidth: 340, flex: '0 0 auto' }}>
              <Card
                variant="outlined"
                sx={{
                  cursor: 'pointer',
                  transition: 'box-shadow 0.25s, transform 0.2s',
                  position: 'relative',
                  borderRadius: 4,
                  boxShadow: 6,
                  background: 'linear-gradient(135deg, #f8fafc 0%, #e3f0ff 100%)',
                  minHeight: 200,
                  p: 0,
                  border: 'none',
                  '&:hover': {
                    boxShadow: 12,
                    transform: 'translateY(-4px) scale(1.02)',
                    background: 'linear-gradient(135deg, #e3f0ff 0%, #f8fafc 100%)',
                  },
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  overflow: 'hidden',
                }}
                onClick={(e) => {
                  if (e.target.closest('.subscribe-icon-btn')) return;
                  if (isSubscribed(chapter)) {
                    setSelectedChapter(chapter);
                    setDialogOpen(true);
                    fetchChapterStudents(chapter.chapterId);
                  } else {
                    setSnackbar({ open: true, message: 'Please join this chapter to view member details.', severity: 'warning' });
                  }
                }}
              >
                {/* Gradient accent bar */}
                <MDBox
                  sx={{
                    height: 8,
                    width: '100%',
                    background: 'linear-gradient(90deg, #1976d2 0%, #43a047 100%)',
                  }}
                />
                {/* Header with avatar, name, and subscribe icon */}
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  px={2}
                  pt={2}
                  pb={1}
                  sx={{
                    background: isSubscribed(chapter) ? '#d0f5e8' : 'transparent',
                    borderRadius: 2,
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <MDBox display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ bgcolor: '#1976d2', width: 44, height: 44, fontWeight: 700, fontSize: 22, boxShadow: 2 }}>
                      {chapter.chapterName ? chapter.chapterName[0] : '?'}
                    </Avatar>
                    <MDTypography
                      variant="h6"
                      color="primary"
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '1.25rem',
                        textShadow: '0 1px 4px rgba(25, 118, 210, 0.08)',
                        letterSpacing: 0.2,
                      }}
                    >
                      {getHighlightedName(chapter.chapterName, filter)}
                    </MDTypography>
                    <Box display="flex" alignItems="center" ml={1} gap={0.5}>
                      <GroupIcon sx={{ color: '#1976d2', fontSize: 22 }} />
                      <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                        {chapter.mc || 0}
                      </Typography>
                    </Box>
                  </MDBox>
                  <Tooltip title={isSubscribed(chapter) ? "Subscribed" : "Subscribe"}>
                    <span>
                      <IconButton
                        className="subscribe-icon-btn"
                        sx={{ color: isSubscribed(chapter) ? '#43a047' : '#fbc02d', fontSize: 30 }}
                        color={isSubscribed(chapter) ? "success" : "warning"}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSubscribe(chapter);
                        }}
                        disabled={subscribingId === chapter.chapterId}
                      >
                        {isSubscribed(chapter) ? <NotificationsActiveIcon fontSize="inherit" /> : <NotificationsNoneIcon fontSize="inherit" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                  {isSubscribed(chapter) && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 8,
                        display: 'flex',
                        alignItems: 'center',
                        opacity: 0.32,
                        pointerEvents: 'none',
                        zIndex: 0,
                      }}
                    >
                      <CheckCircleIcon sx={{ fontSize: 44, color: '#43a047', mr: 1 }} />
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          fontSize: 18,
                          color: '#43a047',
                          letterSpacing: 1,
                          userSelect: 'none',
                        }}
                      >
                        Subscribed
                      </Typography>
                    </Box>
                  )}
                </MDBox>
                <CardContent sx={{ pt: 0, px: 2, pb: 2, background: 'rgba(255,255,255,0.85)' }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    {chapter.location}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                    <strong>Short Name:</strong> {chapter.chapterShortName}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Description:</strong> {chapter.description}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Status:</strong> <span style={{ color: chapter.isActive ? "green" : "red" }}>{chapter.isActive ? "Active" : "Inactive"}</span>
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullScreen
        TransitionComponent={Transition}
      >
        <DialogTitle>
          {selectedChapter ? selectedChapter.chapterName : "Chapter Members"}
          <IconButton
            aria-label="close"
            onClick={() => setDialogOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ background: "#f8fafc" }}>
          {loadingStudents ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
              <CircularProgress />
            </Box>
          ) : (
            selectedChapter && isSubscribed(selectedChapter) ? (
              <Grid container spacing={1.5}>
                {studentList.length === 0 ? (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" align="center">
                      No members found for this chapter.
                    </Typography>
                  </Grid>
                ) : (
                  studentList.map((member, idx) => (
                    <Grid item xs={12} sm={6} md={4} key={member.chapterMemberId}>
                      <Card
                        variant="outlined"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          borderRadius: 2,
                          boxShadow: 1,
                          background: '#fff',
                          border: '1px solid #e3e3e3',
                          mb: 1,
                          p: 1.2,
                          minHeight: 72,
                          transition: 'box-shadow 0.2s, border-color 0.2s',
                          '&:hover': {
                            boxShadow: 4,
                            borderColor: "#1976d2",
                          },
                        }}
                      >
                        <Avatar
                          src={member.img ? getUploadUrl(member.img) : undefined}
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: "#1976d2",
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: 18,
                            mr: 1.5,
                            cursor: "pointer",
                          }}
                          onClick={e => {
                            e.stopPropagation();
                            setProfileDetails(member.img ? getUploadUrl(member.img) : null);
                            setProfileDialogOpen(true);
                          }}
                        >
                          {(!member.img && member.fullName) ? member.fullName[0] : <PersonIcon fontSize="medium" />}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" fontWeight={600} sx={{ color: "#1976d2", mb: 0.2, fontSize: 15 }}>
                            {member.fullName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                            {member.roleName}
                          </Typography>
                          {member.customBatch && (
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12, mt: 0.2, fontStyle: 'italic' }}>
                              Batch: {member.customBatch}
                            </Typography>
                          )}
                          <Box mt={0.2} display="flex" alignItems="center" gap={1}>
                            <Chip
                              label={member.isActive ? "Active" : "Inactive"}
                              color={member.isActive ? "success" : "default"}
                              size="small"
                              sx={{ height: 20, fontSize: 11, px: 1 }}
                            />
                            {member.isCoordinator && (
                              <Chip label="Coordinator" color="info" size="small" sx={{ height: 20, fontSize: 11, px: 1 }} />
                            )}
                          </Box>
                        </Box>

                      </Card>
                      {/* Divider between members except last */}
                      {idx !== studentList.length - 1 && (
                        <Box sx={{ height: 1, background: "#e3e3e3", my: 0.5, borderRadius: 1 }} />
                      )}
                    </Grid>
                  ))
                )}
              </Grid>
            ) : (
              <Box display="flex" alignItems="center" justifyContent="center" minHeight={120}>
                <Typography variant="h6" color="warning.main">
                  Please join this chapter to view member details.
                </Typography>
              </Box>
            )
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setDialogOpen(false)} color="info" variant="outlined">
            Close
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Confirm Subscription Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Join Chapter</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to join this chapter?</Typography>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setConfirmDialogOpen(false)} color="secondary" variant="outlined">
            Cancel
          </MDButton>
          <MDButton onClick={confirmSubscribe} color="info" variant="gradient" disabled={isProcessing}>
            OK
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Unsubscribe Confirmation Dialog */}
      <Dialog open={unsubscribeDialogOpen} onClose={() => setUnsubscribeDialogOpen(false)}>
        <DialogTitle>Unsubscribe from Chapter</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to unsubscribe from this chapter?</Typography>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setUnsubscribeDialogOpen(false)} color="secondary" variant="outlined">
            Cancel
          </MDButton>
          <MDButton onClick={confirmUnsubscribe} color="error" variant="gradient">
            Unsubscribe
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Snackbar Alert for subscribe/unsubscribe */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MuiAlert elevation={6} variant="filled" onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </MuiAlert>
      </Snackbar>

      <Dialog
        open={profileDialogOpen}
        onClose={() => setProfileDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <IconButton
          aria-label="close"
          onClick={() => setProfileDialogOpen(false)}
          sx={{ position: "absolute", right: 8, top: 8, zIndex: 1, color: "#1976d2" }}
        >
          <Close />
        </IconButton>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 340,
            background: "#f8fafc",
            p: 3,
            borderRadius: 4,
            boxShadow: 6,
            mt: 4,
            mb: 4,
          }}
        >
          <Avatar
            src={profileDetails}
            sx={{
              width: 100,
              height: 100,
              bgcolor: "#1976d2",
              color: "#fff",
              fontWeight: 700,
              fontSize: 40,
              mb: 2,
              boxShadow: 3,
            }}
          >
            {!profileDetails && <PersonIcon sx={{ fontSize: 60 }} />}
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#1976d2", mb: 0.5 }}>
            {/* Replace with actual name if available */}
            {selectedChapter && selectedChapter.chapterName}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {/* Replace with actual role if available */}
            Member
          </Typography>
          <Chip
            label="Active"
            color="success"
            size="small"
            sx={{ fontSize: 13, fontWeight: 600, mb: 2 }}
          />
          <Box sx={{ width: "100%", mt: 2 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              {/* Add more profile details here if available */}
              More profile details coming soon.
            </Typography>
          </Box>
        </Box>
      </Dialog>
    </MDBox>
  );
};

export default ChapterList;
